import { useChatWidgetStore } from "@cma/store/chat-widget.store";
import { useBeginChat } from "@queries/chat/useBeginChat";
import { useEditThreadTitle } from "@queries/chat/useEditThreadTitle";
import { useSendStreamMessage } from "@queries/chat/useSendStreamMessage";
import { useMessagesStore } from "@store/messages.store";
import { useThreadsStore } from "@store/threads.store";
import { useUIStore } from "@store/ui.store";
import { useCallback } from "react";

type Props = {
  currentThread?: string | undefined;
  navigateToThread: (id: string | null) => void;
  canAdoptTemporaryWidget?: boolean;
};

export const useChatLogic = (props: Props) => {
  const {
    currentThread,
    navigateToThread,
    canAdoptTemporaryWidget = true,
  } = props;
  const setPromptRefId = useMessagesStore((state) => state.setPromptRefId);
  const promptRefId = useMessagesStore((state) => state.promptRefId);
  const isCreatingThread = useMessagesStore((state) => state.isCreatingThread);
  const setIsCreatingThread = useMessagesStore(
    (state) => state.setIsCreatingThread,
  );
  const movePendingThreadMessages = useMessagesStore(
    (state) => state.movePendingThreadMessages,
  );
  const addThread = useThreadsStore((state) => state.addThread);
  const updateThread = useThreadsStore((state) => state.updateThread);

  const editThreadTitleMutation = useEditThreadTitle();
  const messageInputText = useUIStore((state) => state.messageInputText);
  const activeWidgetContext = useChatWidgetStore(
    (state) => state.activeWidgetContext,
  );
  const chatWidget = useChatWidgetStore((state) => state.chatWidget);
  const temporaryWidgetThreadId = useChatWidgetStore(
    (state) => state.temporaryWidgetThreadId,
  );
  const linkTemporaryWidgetThread = useChatWidgetStore(
    (state) => state.linkTemporaryWidgetThread,
  );

  const beginChatMutation = useBeginChat({
    onSuccess: (data) => {
      if (
        canAdoptTemporaryWidget &&
        chatWidget?.temporary &&
        !temporaryWidgetThreadId
      ) {
        linkTemporaryWidgetThread(data.thread);
      }
      movePendingThreadMessages(data.thread);
      navigateToThread(data.thread);
      messageInputText?.focus();
    },
  });

  const sendMessageMutation = useSendStreamMessage({
    onSuccess: ({ promptRefId }) => {
      setPromptRefId(promptRefId);
      setIsCreatingThread(false);
      if (currentThread) {
        updateThread(currentThread, { updatedAt: new Date() });
      }
    },
    onError: () => {
      setIsCreatingThread(false);
    },
  });

  const handleSendMessage = useCallback(
    async (params: {
      message: string;
      hidden?: boolean;
      threadTitle?: string;
      metadata?: Record<string, string>;
    }): Promise<{ threadId: string }> => {
      const { message, hidden, threadTitle, metadata } = params;
      if (currentThread) {
        sendMessageMutation.mutate({
          threadId: currentThread,
          message,
          hidden,
          activeWidgetContext: activeWidgetContext
            ? activeWidgetContext
            : undefined,
        });
        return { threadId: currentThread };
      }

      setIsCreatingThread(true);
      let data;
      try {
        data = await beginChatMutation.mutateAsync({
          firstMessage: hidden ? undefined : message,
          metadata,
        });
      } catch (error) {
        setIsCreatingThread(false);
        throw error;
      }

      if (threadTitle) {
        try {
          await editThreadTitleMutation.mutateAsync({
            threadId: data.thread,
            title: threadTitle,
          });
        } catch (error) {
          console.error("Error updating thread title:", { error });
        }
      }

      addThread({
        title: threadTitle ? threadTitle : data.title,
        id: data.thread,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        metadata: metadata || null,
        pinned: false,
      });

      sendMessageMutation.mutate({
        threadId: data.thread,
        message,
        hidden,
        activeWidgetContext: activeWidgetContext
          ? activeWidgetContext
          : undefined,
      });

      return { threadId: data.thread };
    },
    [
      addThread,
      beginChatMutation,
      currentThread,
      editThreadTitleMutation,
      sendMessageMutation,
      activeWidgetContext,
      setIsCreatingThread,
    ],
  );

  const isSendingMessage =
    sendMessageMutation.isPending ||
    beginChatMutation.isPending ||
    isCreatingThread;

  return {
    handleSendMessage,
    setPromptRefId,
    isSendingMessage,
    promptRefId,
  };
};
